import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';

/**
 * Fetch user invoices from Stripe via Supabase edge function
 * @returns {Promise<Array>} Array of invoice objects
 */
export const fetchInvoices = async () => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/get-invoices`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to fetch invoices');
    }

    const { invoices } = await response?.json();
    return invoices || [];
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Download invoice PDF
 * @param {string} invoicePdfUrl - URL to the invoice PDF
 * @param {string} invoiceId - Invoice ID for filename
 */
export const downloadInvoice = async (invoicePdfUrl, invoiceId) => {
  try {
    if (!invoicePdfUrl) {
      throw new Error('Invoice PDF URL not available');
    }

    // Open in new tab for download
    window.open(invoicePdfUrl, '_blank');
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw error;
  }
};

/**
 * Generate PDF invoice from invoice data
 * @param {Object} invoice - Invoice object
 * @returns {Blob} PDF blob
 */
export const generateInvoicePDF = (invoice) => {
  try {
    const doc = new jsPDF();
    
    // Header
    doc?.setFontSize(24);
    doc?.setTextColor(59, 130, 246); // Blue color
    doc?.text('DraftIQ', 20, 20);
    
    doc?.setFontSize(10);
    doc?.setTextColor(107, 114, 128); // Gray color
    doc?.text('Invoice', 20, 28);
    
    // Invoice details
    doc?.setFontSize(12);
    doc?.setTextColor(31, 41, 55); // Dark gray
    doc?.text(`Invoice #: ${invoice?.id}`, 20, 45);
    doc?.text(`Date: ${new Date(invoice?.date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 20, 52);
    doc?.text(`Status: ${invoice?.status?.toUpperCase()}`, 20, 59);
    
    // Divider line
    doc?.setDrawColor(229, 231, 235);
    doc?.line(20, 65, 190, 65);
    
    // Description
    doc?.setFontSize(11);
    doc?.setTextColor(75, 85, 99);
    doc?.text('Description:', 20, 75);
    doc?.setTextColor(31, 41, 55);
    doc?.text(invoice?.description || 'Subscription Payment', 20, 82);
    
    // Payment method
    doc?.setTextColor(75, 85, 99);
    doc?.text('Payment Method:', 20, 92);
    doc?.setTextColor(31, 41, 55);
    doc?.text(invoice?.paymentMethod || 'N/A', 20, 99);
    
    // Amount section
    doc?.setDrawColor(229, 231, 235);
    doc?.line(20, 110, 190, 110);
    
    doc?.setFontSize(14);
    doc?.setTextColor(31, 41, 55);
    doc?.text('Amount Paid:', 20, 120);
    
    doc?.setFontSize(20);
    doc?.setTextColor(59, 130, 246);
    doc?.text(`$${invoice?.amount?.toFixed(2)}`, 150, 120);
    
    // Footer
    doc?.setFontSize(9);
    doc?.setTextColor(107, 114, 128);
    doc?.text('Thank you for your business!', 20, 270);
    doc?.text('© 2026 DraftIQ. All rights reserved.', 20, 277);
    
    return doc?.output('blob');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Download invoice as PDF
 * @param {Object} invoice - Invoice object
 */
export const downloadInvoiceAsPDF = (invoice) => {
  try {
    const pdfBlob = generateInvoicePDF(invoice);
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoice?.id}.pdf`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

/**
 * Send invoice email
 * @param {string} userId - User ID
 * @param {Object} invoice - Invoice object
 */
export const sendInvoiceEmail = async (userId, invoice) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-invoice-email`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          invoiceId: invoice?.id,
          invoiceAmount: invoice?.amount,
          invoiceDate: invoice?.date,
          description: invoice?.description,
        }),
      }
    );

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to send invoice email');
    }

    const data = await response?.json();
    return { data, error: null };
  } catch (error) {
    console.error('Error sending invoice email:', error);
    return { data: null, error };
  }
};